import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import parse, { HTMLReactParserOptions, Element } from 'html-react-parser';
import { GlobalData } from '../../../types';
import global from '../../../styles/index.module.scss';
import styles from './Step3.module.scss';
import { Layout } from '../../../components/common/Layout/Layout';
import { StepHeader } from '../../../components/common/StepHeader/StepHeader';
import { ChangeButtonLayout } from '../../../components/common/ChangeButtonLayout/ChangeButtonLayout';
import { redirectTo404 } from '../../../utils/redirectTo404';
import IconBottomArrow from '../../../assets/img/common/icon_bottom_arrow.svg';
import IconPrice from '../../../assets/img/common/icon_price.svg';
import IconShop from '../../../assets/img/common/icon_shop.svg';
import IconFactory from '../../../assets/img/common/icon_factory.svg';
import IconConsultNotice from '../../../assets/img/step3/shop_consult_notice.svg';
import { Campaign } from '../../../components/common/Campaign/Campaign';

interface Step3Props {
  data: GlobalData[];
  firstCollectionHandle: string | null;
  secondCollectionHandle: string | null;
  shopUrl: string;
  campaignTitle?: string;
  campaignSubtitle?: string;
  isCampaign?: boolean;
}

export const Step3: React.FC<Step3Props> = ({ data, firstCollectionHandle, secondCollectionHandle, shopUrl, campaignTitle, campaignSubtitle, isCampaign }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [heights, setHeights] = useState<number[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // HTML parser options for secure content rendering
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element) {
        const { name, attribs } = domNode;
        
        // Allow only specific safe tags
        const allowedTags = ['a', 'strong', 'b', 'em', 'i', 'u', 'span', 'p', 'br'];
        if (!allowedTags.includes(name)) {
          return <></>;
        }
        
        // For links, ensure they are safe
        if (name === 'a' && attribs?.href) {
          // Only allow https and relative links
          if (attribs.href.startsWith('https://') || attribs.href.startsWith('/')) {
            // Return undefined to let the parser handle children normally
            return undefined;
          }
          // Convert unsafe links to spans but preserve content
          return undefined;
        }
        
        // For spans with style attributes, only allow text-decoration
        if (name === 'span' && attribs?.style) {
          const allowedStyles = ['text-decoration'];
          const styles = attribs.style.split(';')
            .filter(style => {
              const [property] = style.split(':');
              return allowedStyles.includes(property.trim());
            })
            .join(';');
          
          if (styles) {
            // Return undefined to let the parser handle children normally with allowed styles
            return undefined;
          }
          // For spans without allowed styles, still preserve content
          return undefined;
        }
      }
      return undefined;
    }
  };

  if (!firstCollectionHandle || !secondCollectionHandle) {
    redirectTo404();
    return null;
  }

  const collectionData = data.find(d => d.first_collection.handle === firstCollectionHandle);
  if (!collectionData) {
    redirectTo404();
    return null;
  }

  const secondCollectionData = collectionData.second_collections.find(sc => sc.second_collection.handle === secondCollectionHandle);
  if (!secondCollectionData) {
    redirectTo404();
    return null;
  }

  // first_otherのデータはオプショナル（コメントアウトされたコードでのみ使用）
  // const collectionOtherData = data.find(d => d.first_collection.handle === "first_other");
  // const collectionProductOtherData = collectionOtherData?.second_collections[0]?.products[0];

  const toggleAccordion = (index: number) => {
    if (openIndex === index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  useEffect(() => {
    const newHeights = contentRefs.current.map(ref => ref?.scrollHeight || 0);
    setHeights(newHeights);
  }, [openIndex, data]);

  return (
    <div className={styles.step3}>
      <Layout>
        <Campaign title={campaignTitle} subtitle={campaignSubtitle} />
      </Layout>
      {firstCollectionHandle !== 'suits_campaign' && (
      <Layout>
        <ChangeButtonLayout
          stepNumber={2}
          firstCollectionHandle={firstCollectionHandle}
          firstCollectionTitle={collectionData.first_collection.title}
          secondCollectionTitle={secondCollectionData.second_collection.title}
        />
      </Layout>
      )}
      <Layout>
        <StepHeader
          stepNumber={3}
          title={<>当てはまる修理を選んでください<br />メニュー例を紹介します</>}
          firstcollectionhandle={firstCollectionHandle}
        />
      </Layout>
      <div className={styles.flex}>
        {secondCollectionData.products.map((productData, productIndex) => {
          const isEstimateDisabled = Boolean(productData.product_metafields.metafields['_estimate-btn-none']);
          const autoShopLink = productData.product_metafields.metafields['_auto-shop-link'];

          const renderShopLink = (isStandalone = false) => {
            if (!autoShopLink) {
              return null;
            }

            const shopLinkClassName = isStandalone ? `${styles.shopLink} ${styles.shopLinkStandalone}` : styles.shopLink;
            const buttonClassName = isStandalone 
              ? global.primaryButton 
              : `${global.primaryButton} ${global.reverse}`;

            return (
              <p className={shopLinkClassName}>
                <a
                  className={buttonClassName}
                  href={autoShopLink}
                  target="_blank"
                >
                  修理受付できる店舗を見る
                </a>
              </p>
            );
          };

          return (
            <div key={productIndex} className={styles.product}>
            <div className={styles.productHeaderWrapper}>
              <button
                className={styles.productHeader}
                onClick={() => toggleAccordion(productIndex)}
                aria-expanded={openIndex === productIndex}
                aria-controls={`product-details-${productIndex}`}
                id={`product-header-${productIndex}`}
              >
                <div className={styles.productHeaderTitleWrapper}>
                  <p className={styles.productHeaderTitle}>{productData.product.title}</p>
                  <p className={styles.productHeaderSubTitle}>{productData.product_metafields.metafields._nayami}</p>
                </div>
                <IconBottomArrow />
              </button>
            </div>
            <div className={styles.productDetailsWrapper}>
              <div className={styles.productDetailsInner}>
                <Layout>
                  <div
                    className={`${styles.productDetails} ${openIndex === productIndex ? styles.open : ''}`}
                    id={`product-details-${productIndex}`}
                    role="region"
                    aria-labelledby={`product-header-${productIndex}`}
                    ref={el => contentRefs.current[productIndex] = el}
                    style={{ height: openIndex === productIndex ? `${heights[productIndex]}px` : '0px' }}
                  >
                    <div className={styles.contentLayout}>
                      <div className={styles.menuDetailWrapper}>
                        <IconPrice />
                        <div className={styles.menuWrapper}>
                          {productData.product.options.map((optionData, optionIndex) => (
                            <div key={optionIndex} className={styles.menuFlex}>
                              <p className={styles.menuName}>{optionData}</p>
                              <p className={styles.menuValue}>{productData.product.variants[0].options[optionIndex]}</p>
                            </div>
                          ))}
                          {isCampaign ? (
                            <p className={styles.CampaignCautionText}>※キャンペーン金額適用前</p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className={styles.contentLayout}>
                      <div className={styles.menuDetailWrapper}>
                        <IconShop />
                        <div className={styles.menuWrapper}>
                          <div className={styles.menuWrapper}>
                            <div className={styles.menuFlex}>
                              <p className={styles.menuName}>納期(店舗)</p>
                              <p className={styles.menuValue}>{productData.product_metafields.metafields['_delivery-date-store']}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.contentLayout}>
                      <div className={styles.menuDetailWrapper}>
                        <IconFactory />
                        <div className={styles.menuWrapper}>
                          <div className={styles.menuWrapper}>
                            <div className={styles.menuFlex}>
                              <p className={styles.menuName}>納期(配送修理)<br />※ お支払い完了後</p>
                              <p className={styles.menuValue}>
                                {productData.product_metafields.metafields['_delivery-date-home-delivery']}
                                <span>※ 納期はいずれも目安です。修理品の状態・修理の内容により異なります。</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {isCampaign ? (
                      <div className={styles.contentLayout}>
                        <p className={styles.CampaignCautionTitle}>配送修理をお申し込みいただいた後、最終お見積りの段階でキャンペーン適用の価格をご提示いたします。</p>
                        {/* <p className={styles.CampaignCautionSubText}>・後日キャンペーン適用後の見積もりをご提示いたします。</p> */}
                      </div>
                      ) : null}
                    {/* <div className={`${global.buttonWrapper} ${global.isWide}`}>
                      {(() => {
                        // 現在のURLパラメータを取得
                        const currentParams = new URLSearchParams(window.location.search);
                        currentParams.set('step', '4');
                        currentParams.set('firstcollectionhandle', collectionData.first_collection.handle);
                        currentParams.set('secondcollectionhandle', secondCollectionData.second_collection.handle);
                        currentParams.set('producthandle', productData.product.handle);
                        const linkPath = `/?${currentParams.toString()}`;
                        
                        return (
                          <Link
                            to={linkPath}
                            className={`${global.primaryButton} ${global.reverse} ${productData.product_metafields.metafields._ga_id ? "js-trackEvent" : ""}`}
                            data-ga-id={productData.product_metafields.metafields._ga_id}
                          >
                            診断・見積もりに進む
                          </Link>
                        );
                      })()}
                    </div> */}
                    {productData.product.content && (
                      <ul className={styles.descriptionList}>
                        {productData.product.content
                          .split(/<\/?p>/)
                          .filter(line => line.trim() !== '')
                          .map((line, index) => (
                            <li key={index}>{parse(line, parserOptions)}</li>
                          ))}
                      </ul>
                    )}
                    {productData.product_metafields.metafields['_precaution1'] && (
                      <ul className={styles.cautionList}>
                        {productData.product_metafields.metafields['_precaution1'].split('\n').map((line, index) => (
                          <li key={index}>{line}</li>
                        ))}
                      </ul>
                    )}
                    {productData.product_metafields.menu_img && (
                      <div className={styles.productImg}>
                        <img src={`${shopUrl}/cdn/shop/${productData.product_metafields.menu_img}`} alt="" />
                      </div>
                    )}
                    {productData.product_metafields.metafields['_related-url'] && (
                      <a href={productData.product_metafields.metafields['_related-url']} className={styles.repairedUrl} target="_blank">実際の修理事例（Before-After）を見る</a>
                    )}
                    {isEstimateDisabled ? (
                      <>
                      <div className={styles.noticeSection}>
                        <div className={styles.noticeWrapper}>
                          <div className={styles.noticeHeader}>
                            <p className={styles.noticeTitle}>
                              このメニューは<br />
                              画像による診断・見積りができません。
                            </p>
                            <p className={styles.noticeDescription}>※配送修理のお申し込みもできません。</p>
                          </div>
                          <div className={styles.noticeCta}>
                            <span className={styles.noticeIcon} aria-hidden="true">
                              <IconConsultNotice />
                            </span>
                            <p className={styles.noticeMessage}>
                              お店でご相談ください。<br />
                              ご来店お待ちしています。
                            </p>
                          </div>
                        </div>
                        {renderShopLink(true)}
                      </div>
                      </>
                    ) : (
                      <div className={styles.lineWrapper}>
                        <p className={styles.text}>
                          表示されているメニューは一例です。<br />
                          実際に修理できるかどうかは、次に進んで画像を送ってください。<br />
                          修理スタッフが診断しメールで回答いたします。
                        </p>
                        <div className={`${styles.lineButtonWrapper}`}>
                        {(() => {
                          // 現在のURLパラメータを取得
                          const currentParams = new URLSearchParams(window.location.search);
                          currentParams.set('step', '4');
                          currentParams.set('firstcollectionhandle', collectionData.first_collection.handle);
                          currentParams.set('secondcollectionhandle', secondCollectionData.second_collection.handle);
                          currentParams.set('producthandle', productData.product.handle);
                          const linkPath = `/?${currentParams.toString()}`;
                          return (
                            <div>
                              <Link
                                to={linkPath}
                                className={`${global.primaryButton} ${productData.product_metafields.metafields._ga_id ? "js-trackEvent" : ""}`}
                                data-ga-id={productData.product_metafields.metafields._ga_id}
                              >
                                診断・見積もりに進む
                              </Link>
                              <p className={styles.repairLink}>配送修理もこちらからお進みください</p>
                            </div>
                          );
                        })()}
                        {renderShopLink()}
                      </div>
                    </div>
                    )}
                  </div>
                </Layout>
              </div>
            </div>
          </div>
          );
        })}
        {/* {firstCollectionHandle !== 'suits_campaign' && collectionProductOtherData && (
          <div className={styles.product}>
            <div className={styles.productHeaderWrapper}>
              {(() => {
                // 現在のURLパラメータを取得
                const currentParams = new URLSearchParams(window.location.search);
                currentParams.set('step', '4');
                currentParams.set('firstcollectionhandle', collectionData.first_collection.handle);
                currentParams.set('secondcollectionhandle', secondCollectionData.second_collection.handle);
                currentParams.set('producthandle', collectionProductOtherData.product.handle);
                const linkPath = `/?${currentParams.toString()}`;
                
                return (
                  <Link
                    to={linkPath}
                    className={`${styles.productHeader} ${`Clk_Btn_AppPage3_${secondCollectionHandle}_others` ? "js-trackEvent" : ""}`}
                    data-ga-id={`Clk_Btn_AppPage3_${secondCollectionHandle}_others`}
                  >
                    <div className={styles.productHeaderTitleWrapper}>
                      <p className={styles.productHeaderTitle}>{collectionProductOtherData.product.title}</p>
                      <p className={styles.productHeaderSubTitle}>{collectionProductOtherData.product_metafields.metafields._nayami}</p>
                    </div>
                    <IconRightArrow />
                  </Link>
                );
              })()}
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};
